import { createElement, useId, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Download,
  ExternalLink as ExternalLinkIcon,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Link2,
  Lock,
  Paperclip,
  Plus,
  Presentation,
  Trash2,
  Upload as UploadIcon,
} from 'lucide-react'
import { api, extractApiError, isPlanLimitError } from '../lib/api'
import { CONTAINS_EMOJI } from '../lib/validators'
import { ConfirmDialog } from './ui/ConfirmDialog'
import VisibilityPills from './VisibilityPills'
import { useSpaceAccess } from '../lib/useSpaceAccess'
import {
  UPLOAD_ACCEPT_ATTR,
  UPLOAD_EXTENSIONS_TEXT,
  isAllowedUploadFile,
} from '../lib/upload-extensions'
import { useMaxUploadBytes } from '../lib/useMaxUploadBytes'
import { formatDateOnly, formatNumber } from '../lib/format'

/**
 * The Space's "Files" tab — the missing surface that lets a file (or link)
 * uploaded to the WHOLE Space (from the Files & Links module, or here) show up
 * in Space Explorer. Files attached to an individual task appear on that task;
 * files scoped to the Space (`File.spaceId`) had no home in the Space view
 * until now. Reads the same space-scoped endpoints the backend already
 * supports (`GET /files?spaceId=`, `GET /external-links?spaceId=`) and offers
 * upload / add-link / open / download / delete for edit-or-higher members.
 */

/** Link title — 2–120 chars, must contain a LETTER (so numbers-only or
 *  symbols-only titles are rejected) and no emoji. Mirrors the per-task Files &
 *  Links + Folder Name validation so every surface agrees. */
function validateLinkTitle(value) {
  const t = value.trim()
  if (t.length === 0) return 'Title is required.'
  if (t.length < 2) return 'Title must be at least 2 characters.'
  if (t.length > 120) return 'Title must be 120 characters or fewer.'
  if (CONTAINS_EMOJI.test(t)) return 'Title cannot contain emojis.'
  if (!/\p{L}/u.test(t)) return 'Title must contain at least one letter.'
  return null
}

/** Prepend https:// when the user omits a scheme. */
function normalizeLinkUrl(value) {
  const t = value.trim()
  if (!t) return ''
  return /^https?:\/\//i.test(t) ? t : `https://${t}`
}

function validateLinkUrl(value) {
  const t = value.trim()
  if (!t) return 'URL is required.'
  let parsed
  try {
    parsed = new URL(normalizeLinkUrl(t))
  } catch {
    return 'Please enter a valid URL (e.g. https://example.com).'
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return 'URL must start with http:// or https://.'
  }
  const host = parsed.hostname
  if (!host.includes('.') || host.startsWith('.') || host.endsWith('.')) {
    return 'Please enter a valid URL (e.g. https://example.com).'
  }
  // Reject all-numeric hosts (e.g. "12.34") that aren't a real IPv4 address —
  // the host must end in an alphabetic TLD, or be a valid IPv4 / localhost.
  const isIpv4 = /^(\d{1,3}\.){3}\d{1,3}$/.test(host)
  const hasAlphaTld = /\.[a-zA-Z]{2,}$/.test(host)
  if (host !== 'localhost' && !isIpv4 && !hasAlphaTld) {
    return 'Please enter a valid URL (e.g. https://example.com).'
  }
  return null
}

function iconForFile(mimeType) {
  const mt = String(mimeType ?? '').toLowerCase()
  if (mt.startsWith('image/')) return ImageIcon
  if (mt.includes('spreadsheet') || mt === 'text/csv') return FileSpreadsheet
  if (mt.includes('presentation')) return Presentation
  if (mt.startsWith('application/pdf') || mt.startsWith('text/')) return FileText
  return FileIcon
}

/** Human-readable size — matches the Files & Links page's terse form. */
function formatBytes(n) {
  const bytes = Number(n) || 0
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${formatNumber(kb, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })} KB`
  const mb = kb / 1024
  if (mb < 1024) return `${formatNumber(mb, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })} MB`
  return `${formatNumber(mb / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })} GB`
}

/** Compact "Private" chip shown on a row. Only rendered for private items —
 *  public is the default and needs no marker, keeping this dense list clean. */
function PrivateBadge({ kind = 'file' }) {
  return (
    <span
      title={
        kind === 'link'
          ? 'Private — only you can see this link'
          : 'Private — only you and your Company Admin or Sub Admin can see this file'
      }
      className="inline-flex items-center gap-0.5 shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]"
    >
      <Lock size={10} />
      Private
    </span>
  )
}

/**
 * @param {object}  props
 * @param {string}  props.spaceId
 * @param {string?} [props.listId] When set, the tab shows only THIS TaskList's
 *   files and links, plus a separate, clearly-labelled "Space files" group for
 *   the Space-level ones that belong to no List. Omit it for the whole-Space
 *   view, which keeps showing everything.
 */
export function SpaceFilesView({ spaceId, listId = null }) {
  const qc = useQueryClient()
  // Read-only members (Space access = view / comment) can open files + links
  // but not upload, add links, or delete. Fails open while loading — the
  // backend still enforces the real gate.
  const { canModify } = useSpaceAccess(spaceId)
  // Real operator-configured per-file cap (not the 2 GB ceiling) so an
  // over-cap file is rejected instantly instead of streaming until Multer
  // aborts mid-flight (which resets the connection → "Can't reach the server").
  const maxUploadBytes = useMaxUploadBytes()
  const fileInputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [addingLink, setAddingLink] = useState(false)
  const [linkTitle, setLinkTitle] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  // Per-link visibility — matches the Files & Links page (default Public).
  const [linkVisibility, setLinkVisibility] = useState('public')
  // A file chosen in the OS picker, held so the user can pick a visibility
  // BEFORE the upload fires (mirrors the Files & Links page's upload dialog —
  // the old quick-picker uploaded immediately with no Public/Private choice).
  const [pendingFile, setPendingFile] = useState(null)
  const [fileVisibility, setFileVisibility] = useState('public')
  // { kind: 'file' | 'link', id, label } — drives the delete confirm modal.
  const [confirmTarget, setConfirmTarget] = useState(null)
  // Per-mount id prefix for the add-link form's label/input pairs. `useId()`
  // rather than a literal: this view can be mounted more than once on a page
  // and duplicate DOM ids would cross-wire the labels.
  const uid = useId()
  const titleId = `${uid}-title`
  const urlId = `${uid}-url`
  const titleErrorId = `${uid}-title-error`
  const urlErrorId = `${uid}-url-error`

  // Keys are prefixed with the SAME `['files']` / `['external-links']` roots
  // the rest of the app invalidates broadly (task attachments, the Files &
  // Links page), so an upload/delete anywhere refreshes this tab and vice
  // versa.
  // `listId` is part of the key, not just the request: without it React Query
  // would serve List A's cached rows while List B's fetch is in flight, which
  // is the very cross-List bleed this scoping exists to stop.
  const filesQuery = useQuery({
    queryKey: ['files', 'space', spaceId, listId ?? 'all'],
    queryFn: async () => {
      const res = await api.get('/files', {
        params: { spaceId, ...(listId ? { listId } : {}) },
      })
      return res.data
    },
    enabled: Boolean(spaceId),
    staleTime: 30_000,
  })
  const linksQuery = useQuery({
    queryKey: ['external-links', 'space', spaceId, listId ?? 'all'],
    queryFn: async () => {
      const res = await api.get('/external-links', {
        params: { spaceId, ...(listId ? { listId } : {}) },
      })
      return res.data
    },
    enabled: Boolean(spaceId),
    staleTime: 30_000,
  })

  // Space-level rows — stamped with this Space but no List. Fetched only in
  // the per-List view, where they'd otherwise vanish; the whole-Space view
  // already includes them in the queries above. `listId=none` is the server's
  // sentinel for "unfiled only" (an absent param means "everything").
  const spaceFilesQuery = useQuery({
    queryKey: ['files', 'space', spaceId, 'space-level'],
    queryFn: async () => {
      const res = await api.get('/files', {
        params: { spaceId, listId: 'none' },
      })
      return res.data
    },
    enabled: Boolean(spaceId) && Boolean(listId),
    staleTime: 30_000,
  })
  const spaceLinksQuery = useQuery({
    queryKey: ['external-links', 'space', spaceId, 'space-level'],
    queryFn: async () => {
      const res = await api.get('/external-links', {
        params: { spaceId, listId: 'none' },
      })
      return res.data
    },
    enabled: Boolean(spaceId) && Boolean(listId),
    staleTime: 30_000,
  })

  const filesList = filesQuery.data?.data ?? []
  const linksList = linksQuery.data?.data ?? []
  const spaceFilesList = listId ? (spaceFilesQuery.data?.data ?? []) : []
  const spaceLinksList = listId ? (spaceLinksQuery.data?.data ?? []) : []
  const loading =
    filesQuery.isLoading ||
    linksQuery.isLoading ||
    spaceFilesQuery.isLoading ||
    spaceLinksQuery.isLoading
  const isEmpty =
    filesList.length === 0 &&
    linksList.length === 0 &&
    spaceFilesList.length === 0 &&
    spaceLinksList.length === 0

  function invalidateAll() {
    void qc.invalidateQueries({ queryKey: ['files'] })
    void qc.invalidateQueries({ queryKey: ['external-links'] })
    void qc.invalidateQueries({ queryKey: ['files', 'storage'] })
  }

  // Called when the user picks a file in the OS dialog. Validate early, then
  // hold the file so they can choose Public / Private before it uploads.
  function onFilePicked(file) {
    if (!file) return
    if (!isAllowedUploadFile(file)) {
      toast.error(
        `This file type is not supported. Allowed extensions: ${UPLOAD_EXTENSIONS_TEXT}.`,
      )
      return
    }
    if (file.size > maxUploadBytes) {
      toast.error(
        `File is too large. Maximum size is ${Math.floor(maxUploadBytes / 1024 / 1024)} MB.`,
      )
      return
    }
    setFileVisibility('public')
    setPendingFile(file)
  }

  async function uploadFile(file, visibility = 'public') {
    if (!file) return
    setBusy(true)
    try {
      const form = new FormData()
      form.append('file', file)
      // Stamp the Space so the file lands on THIS tab (and the module).
      if (spaceId) form.append('spaceId', spaceId)
      // …and the List, when the tab is open inside one, so the file belongs
      // to that List rather than to every List in the Space.
      if (listId) form.append('listId', listId)
      form.append('visibility', visibility)
      await api.post('/files', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 10 * 60 * 1000,
      })
      toast.success('File uploaded successfully.')
      setPendingFile(null)
      invalidateAll()
    } catch (err) {
      if (!isPlanLimitError(err)) toast.error(extractApiError(err).message)
    } finally {
      setBusy(false)
    }
  }

  async function addLink() {
    if (validateLinkTitle(linkTitle) || validateLinkUrl(linkUrl)) return
    setBusy(true)
    try {
      await api.post('/external-links', {
        title: linkTitle.trim(),
        url: normalizeLinkUrl(linkUrl),
        visibility: linkVisibility,
        ...(spaceId ? { spaceId } : {}),
        ...(listId ? { listId } : {}),
      })
      toast.success('Link added successfully.')
      setLinkTitle('')
      setLinkUrl('')
      setLinkVisibility('public')
      setAddingLink(false)
      invalidateAll()
    } catch (err) {
      toast.error(extractApiError(err).message)
    } finally {
      setBusy(false)
    }
  }

  const deleteMutation = useMutation({
    mutationFn: async (target) =>
      target.kind === 'link'
        ? api.delete(`/external-links/${target.id}`)
        : api.delete(`/files/${target.id}`),
    onSuccess: (_data, target) => {
      invalidateAll()
      setConfirmTarget(null)
      toast.success(
        target.kind === 'link'
          ? 'Link deleted successfully.'
          : 'File moved to Trash.',
      )
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  async function downloadFile(id) {
    try {
      // `?download=1` → Content-Disposition: attachment, so the browser SAVES
      // the file instead of previewing images / PDFs in a new tab. A
      // programmatic anchor click (vs window.open) downloads without flashing
      // a blank tab.
      const res = await api.get(`/files/${id}/download`, {
        params: { download: 1 },
      })
      const url = res.data?.url
      if (url) {
        const a = document.createElement('a')
        a.href = url
        a.rel = 'noopener noreferrer'
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
    } catch (err) {
      toast.error(extractApiError(err).message)
    }
  }

  const titleErr = validateLinkTitle(linkTitle)
  const urlErr = validateLinkUrl(linkUrl)

  // Row markup lives in these two helpers so the List group and the
  // "Space files" group render identically instead of drifting apart. Plain
  // functions, not nested components — a nested component would be a new type
  // on every render and would remount its whole subtree.
  function fileSection(heading, rows) {
    if (rows.length === 0) return null
    return (
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-ink-muted)] mb-2">
          {heading} ({rows.length})
        </h3>
        <ul className="rounded-[10px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] divide-y divide-[color:var(--color-rule)] overflow-hidden">
          {rows.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-[color:var(--color-card-hover)]"
            >
              <span className="h-8 w-8 rounded-md bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] flex items-center justify-center text-[color:var(--color-ink-muted)] shrink-0">
                {createElement(iconForFile(f.mimeType), { size: 15 })}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[color:var(--color-ink-strong)] truncate m-0 flex items-center gap-1.5">
                  <span className="truncate">{f.name}</span>
                  {f.visibility === 'private' ? <PrivateBadge /> : null}
                </p>
                <p className="text-[11px] text-[color:var(--color-ink-muted)] truncate m-0">
                  {formatBytes(f.sizeBytes)} · {f.ownerName}
                  {f.createdAt ? ` · ${formatDateOnly(f.createdAt)}` : ''}
                  {/* Which task this file is attached to (blank for
                   *  Space-level uploads with no task link). */}
                  {f.tasks?.length
                    ? ` · Task: ${f.tasks[0].name}${
                        f.tasks.length > 1 ? ` +${f.tasks.length - 1}` : ''
                      }`
                    : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => downloadFile(f.id)}
                aria-label="Download"
                title="Download"
                className="p-1.5 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer"
              >
                <Download size={15} />
              </button>
              {canModify ? (
                <button
                  type="button"
                  onClick={() =>
                    setConfirmTarget({ kind: 'file', id: f.id, label: f.name })
                  }
                  aria-label="Delete"
                  title="Delete"
                  className="p-1.5 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-canvas)] cursor-pointer"
                >
                  <Trash2 size={15} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    )
  }

  function linkSection(heading, rows) {
    if (rows.length === 0) return null
    return (
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-ink-muted)] mb-2">
          {heading} ({rows.length})
        </h3>
        <ul className="rounded-[10px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] divide-y divide-[color:var(--color-rule)] overflow-hidden">
          {rows.map((l) => (
            <li
              key={l.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-[color:var(--color-card-hover)]"
            >
              <span className="h-8 w-8 rounded-md bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] flex items-center justify-center text-[color:var(--color-ink-muted)] shrink-0">
                <Link2 size={15} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[color:var(--color-ink-strong)] truncate m-0 flex items-center gap-1.5">
                  <span className="truncate">{l.title}</span>
                  {l.visibility === 'private' ? <PrivateBadge kind="link" /> : null}
                </p>
                <p className="text-[11px] text-[color:var(--color-ink-muted)] truncate m-0">
                  {l.domain || l.url}
                  {l.ownerName ? ` · ${l.ownerName}` : ''}
                  {l.createdAt ? ` · ${formatDateOnly(l.createdAt)}` : ''}
                </p>
              </div>
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open link"
                title="Open link"
                className="p-1.5 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer"
              >
                <ExternalLinkIcon size={15} />
              </a>
              {canModify ? (
                <button
                  type="button"
                  onClick={() =>
                    setConfirmTarget({ kind: 'link', id: l.id, label: l.title })
                  }
                  aria-label="Delete"
                  title="Delete"
                  className="p-1.5 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-canvas)] cursor-pointer"
                >
                  <Trash2 size={15} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    )
  }

  const hasSpaceLevel = spaceFilesList.length > 0 || spaceLinksList.length > 0

  return (
    <div className="pb-6">
      {/* Header + actions */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 text-[color:var(--color-ink-strong)]">
          <Paperclip size={16} className="text-[color:var(--color-accent-strong)]" />
          <h2 className="font-display text-lg m-0">Files &amp; Links</h2>
        </div>
        {canModify ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UploadIcon size={14} />
              Upload
            </button>
            <button
              type="button"
              onClick={() => setAddingLink((v) => !v)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium bg-[color:var(--color-accent-strong)] text-white hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={14} />
              Add Link
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={UPLOAD_ACCEPT_ATTR}
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) onFilePicked(f)
                if (e.target) e.target.value = ''
              }}
            />
          </div>
        ) : null}
      </div>

      {/* Pending-file panel — choose Public / Private before the upload fires.
          Mirrors the Files & Links page upload dialog so a file dropped into a
          Space can be made Private too. */}
      {canModify && pendingFile ? (
        <div className="mb-4 rounded-[10px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
          <div className="flex items-center gap-2 mb-3 text-sm text-[color:var(--color-ink-strong)]">
            <UploadIcon size={14} className="text-[color:var(--color-accent-strong)] shrink-0" />
            <span className="font-medium truncate" title={pendingFile.name}>
              {pendingFile.name}
            </span>
          </div>
          <VisibilityPills
            value={fileVisibility}
            onChange={setFileVisibility}
            kind="file"
          />
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setPendingFile(null)}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => uploadFile(pendingFile, fileVisibility)}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-[color:var(--color-accent-strong)] text-white hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      ) : null}

      {/* Add-link inline form */}
      {canModify && addingLink ? (
        <div className="mb-4 rounded-[10px] border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Unwrapped from a `<label>` that WRAPPED the input: a wrapping
                label associates implicitly, so its ENTIRE box forwards clicks
                to the control — the empty space beside the caption, the gap
                above the input and the validation line below it all focused
                the field, which is why clicking above the textbox started
                accepting input (QA). Same shape `FieldLabel`
                (ui/Field.jsx) uses: a `block w-fit` label + `htmlFor`, so the
                hit target matches the caption text the user can actually see
                and screen readers keep the association. */}
            <div>
              <label
                htmlFor={titleId}
                className="block w-fit text-[12px] font-medium text-[color:var(--color-ink-strong)] mb-1"
              >
                Title<span aria-hidden="true" className="ml-0.5 text-[color:var(--color-priority-urgent)]">*</span>
              </label>
              <input
                id={titleId}
                aria-describedby={linkTitle.trim() && titleErr ? titleErrorId : undefined}
                type="text"
                value={linkTitle}
                onChange={(e) => setLinkTitle(e.target.value)}
                placeholder="Enter a Title"
                maxLength={120}
                className={
                  'w-full rounded-lg border bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink-strong)] focus:outline-none ' +
                  (linkTitle.trim() && titleErr
                    ? 'border-[color:var(--color-priority-urgent)]'
                    : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent-border)]')
                }
              />
              {linkTitle.trim() && titleErr ? (
                <p id={titleErrorId} className="mt-1 text-[11px] text-[color:var(--color-priority-urgent)]">
                  {titleErr}
                </p>
              ) : null}
            </div>
            <div>
              <label
                htmlFor={urlId}
                className="block w-fit text-[12px] font-medium text-[color:var(--color-ink-strong)] mb-1"
              >
                URL<span aria-hidden="true" className="ml-0.5 text-[color:var(--color-priority-urgent)]">*</span>
              </label>
              <input
                id={urlId}
                aria-describedby={linkUrl.trim() && urlErr ? urlErrorId : undefined}
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className={
                  'w-full rounded-lg border bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink-strong)] focus:outline-none ' +
                  (linkUrl.trim() && urlErr
                    ? 'border-[color:var(--color-priority-urgent)]'
                    : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent-border)]')
                }
              />
              {linkUrl.trim() && urlErr ? (
                <p id={urlErrorId} className="mt-1 text-[11px] text-[color:var(--color-priority-urgent)]">
                  {urlErr}
                </p>
              ) : null}
            </div>
          </div>
          <div className="mt-3">
            <VisibilityPills
              value={linkVisibility}
              onChange={setLinkVisibility}
              kind="link"
              privateNote="Only you can see this link — a private link has no admin override."
            />
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setAddingLink(false)
                setLinkTitle('')
                setLinkUrl('')
                setLinkVisibility('public')
              }}
              className="px-3 py-1.5 rounded-md text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={addLink}
              disabled={busy || Boolean(titleErr) || Boolean(urlErr)}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-[color:var(--color-accent-strong)] text-white hover:opacity-90 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Saving…' : 'Save Link'}
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p className="py-10 text-center text-sm italic text-[color:var(--color-ink-muted)]">
          Loading…
        </p>
      ) : isEmpty ? (
        <div className="rounded-[10px] border border-dashed border-[color:var(--color-rule)] bg-[color:var(--color-card)] py-12 text-center">
          <Paperclip
            size={26}
            className="mx-auto mb-3 text-[color:var(--color-ink-subtle)]"
          />
          <p className="text-sm text-[color:var(--color-ink-muted)] m-0">
            No files or links on this Space yet.
          </p>
          {canModify ? (
            <p className="mt-1 text-xs text-[color:var(--color-ink-subtle)] m-0">
              Upload a file or add a link — it appears here and in Files &amp; Links.
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-6">
          {/* The open List's own files + links. In the whole-Space view (no
            *  listId) these queries return everything, so the headings stay
            *  the plain "Files" / "External links" they always were. */}
          {fileSection('Files', filesList)}
          {linkSection('External links', linksList)}

          {/* Space-level files — stamped with this Space but no List, so they
            *  belong to none of them. Shown in their own labelled group rather
            *  than merged into the open List's, so nothing disappears and
            *  nothing is attributed to a List it was never filed under. */}
          {hasSpaceLevel ? (
            <div className="space-y-6 pt-2 border-t border-[color:var(--color-rule)]">
              <p className="text-[11px] text-[color:var(--color-ink-muted)] m-0 pt-4">
                Shared across this Space — not filed under any list.
              </p>
              {fileSection('Space files', spaceFilesList)}
              {linkSection('Space links', spaceLinksList)}
            </div>
          ) : null}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title={
          confirmTarget?.kind === 'link' ? 'Delete This Link?' : 'Delete This File?'
        }
        message={
          confirmTarget
            ? confirmTarget.kind === 'link'
              ? `"${confirmTarget.label}" will be permanently removed from this Space and Files & Links. This cannot be undone.`
              : `"${confirmTarget.label}" will be moved to Trash, where it can be restored until it is permanently purged.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteMutation.isPending}
        onCancel={() =>
          deleteMutation.isPending ? undefined : setConfirmTarget(null)
        }
        onConfirm={() => {
          if (confirmTarget?.id) deleteMutation.mutate(confirmTarget)
        }}
      />
    </div>
  )
}
